'use client';

import { CMSSection } from '@/components/cms/CmsSection';
import { Heading, type HeadingData } from '@/components/cms/Heading';
import type { RichTextContent } from '@/lib/contentful/types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Document } from '@contentful/rich-text-types';
import clsx from 'clsx';
import { Link } from '@/lib/i18n/navigation';
import type { JSX } from 'react';
import styles from './CollectionInfoParameter.module.scss';

export interface CollectionInfoParameterProps {
  /** Internal name for CMS reference */
  internalName?: string;
  /** Heading data for the collection title */
  placeholderHeading?: HeadingData | null;
  /** Rich text content for the card */
  cardText?: RichTextContent | null;
  /** Content alignment (left, center, right) */
  contentAlignment?: string | null;
  /** Text alignment within content */
  textAlignment?: string | null;
  /** CTA URL */
  url?: string | null;
  /** CTA button text */
  urlText?: string | null;
  /** CTA type (button, link) */
  urlType?: string | null;
  /** Whether this is a hero card */
  isHeroCard?: boolean;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

/**
 * CollectionInfoParameter - Collection info section
 *
 * Displays collection-level information with a heading, rich text content,
 * and optional CTA. Used on collection/category landing pages.
 *
 * @example
 * <CollectionInfoParameter
 *   placeholderHeading={{ text: "Our Heritage Collection" }}
 *   cardText={{ json: richTextJson }}
 *   urlText="Explore Collection"
 *   url="/collections/heritage"
 *   cmsIndex={0}
 * />
 */
export function CollectionInfoParameter({
  placeholderHeading,
  cardText,
  contentAlignment,
  textAlignment,
  url,
  urlText,
  urlType,
  isHeroCard,
  cmsIndex,
  className,
}: CollectionInfoParameterProps): JSX.Element {
  const testId = (element?: string): string | undefined =>
    cmsIndex !== undefined
      ? element
        ? `collectioninfoparameter-${cmsIndex}-${element}`
        : `collectioninfoparameter-${cmsIndex}`
      : undefined;

  const alignmentClass = contentAlignment?.toLowerCase() || 'center';
  const textAlignClass = textAlignment?.toLowerCase() || 'center';

  const renderHeading = (): JSX.Element | null => {
    if (!placeholderHeading) return null;
    return (
      <div className={styles.headingWrapper} data-testid={testId('heading')}>
        <Heading Heading={placeholderHeading} sizeDesktop={50} sizeMobile={40} />
      </div>
    );
  };

  const renderCardText = (): JSX.Element | null => {
    if (!cardText?.json) return null;
    return (
      <div className={styles.text} data-testid={testId('text')}>
        {documentToReactComponents(cardText.json as Document)}
      </div>
    );
  };

  const renderCta = (): JSX.Element | null => {
    if (!urlText || !url) return null;
    return urlType === 'Button' ? (
      <Link href={url} className={styles.ctaButton} data-testid={testId('cta')}>
        {urlText}
      </Link>
    ) : (
      <Link href={url} className={styles.ctaLink} data-testid={testId('cta')}>
        {urlText}
      </Link>
    );
  };

  return (
    <CMSSection className={clsx(styles.section, className)} data-testid={testId()}>
      <div
        className={clsx(
          styles.wrapper,
          styles[`align${alignmentClass}`],
          isHeroCard && styles.heroCard
        )}
        data-testid={testId('wrapper')}
      >
        <div
          className={clsx(styles.content, styles[`text${textAlignClass}`])}
          data-testid={testId('content')}
        >
          {renderHeading()}
          {renderCardText()}
          {renderCta()}
        </div>
      </div>
    </CMSSection>
  );
}

CollectionInfoParameter.displayName = 'CollectionInfoParameter';
